/**
 * Made to Measure Handler
 * Shows custom measurement fields when "Made to Measure" variant is selected
 */

class MadeToMeasureHandler {
  constructor() {
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupListeners());
    } else {
      this.setupListeners();
    }
  }

  setupListeners() {
    // Listen for variant changes on all variant pickers
    document.addEventListener('change', (event) => {
      const target = event.target;
      
      // Check if this is a variant option input
      if (target.matches('input[type="radio"][data-option-position]') || 
          target.matches('input[type="checkbox"][data-option-position]')) {
        this.handleVariantChange(target);
      }
    });

    // Also listen for click events on variant links (for combined products)
    document.addEventListener('click', (event) => {
      const link = event.target.closest('a[data-product-url]');
      if (link) {
        // Let the link navigate normally, measurements will be shown on new page if needed
        return;
      }
    });

    // Initial check on page load
    this.checkAllVariantPickers();
  }

  checkAllVariantPickers() {
    const variantPickers = document.querySelectorAll('variant-picker');
    variantPickers.forEach(picker => {
      const selectedInput = picker.querySelector('input[type="radio"]:checked, input[type="checkbox"]:checked');
      if (selectedInput) {
        this.handleVariantChange(selectedInput);
      }
    });
  }

  handleVariantChange(input) {
    const variantPicker = input.closest('variant-picker');
    if (!variantPicker) return;

    const formId = variantPicker.getAttribute('form-id');
    if (!formId) return;

    // Get the label text for this option
    const label = input.closest('label, .variant-picker__option-values')
      ?.querySelector(`label[for="${input.id}"]`)
      ?.textContent.trim() || input.value;

    // Check if "Made to Measure" is selected
    const isMadeToMeasure = this.isMadeToMeasureOption(label, input);

    // Show or hide measurement fields
    this.toggleMeasurementFields(formId, isMadeToMeasure);
  }

  isMadeToMeasureOption(label, input) {
    // Check if the label contains "Made to Measure" (case-insensitive)
    const labelLower = label.toLowerCase();
    return labelLower.includes('made to measure') || 
           labelLower.includes('custom') ||
           labelLower.includes('bespoke');
  }

  toggleMeasurementFields(formId, show) {
    let container = document.getElementById(`measurements-${formId}`);

    if (show && !container) {
      // Create the measurement fields container
      container = this.createMeasurementFields(formId);
      
      // Find the variant picker and insert after it
      const variantPicker = document.querySelector(`variant-picker[form-id="${formId}"]`);
      if (variantPicker) {
        variantPicker.insertAdjacentElement('afterend', container);
      }
    }

    if (container) {
      container.style.display = show ? 'block' : 'none';
      
      // Update required attribute on inputs
      const inputs = container.querySelectorAll('input');
      inputs.forEach(input => {
        if (show) {
          input.setAttribute('required', 'required');
        } else {
          input.removeAttribute('required');
          input.value = ''; // Clear values when hidden
        }
      });
    }
  }

  createMeasurementFields(formId) {
    const container = document.createElement('div');
    container.id = `measurements-${formId}`;
    container.className = 'made-to-measure-fields v-stack gap-4';
    container.style.display = 'none';

    container.innerHTML = `
      <div class="made-to-measure-header">
        <h3 class="h6">Custom Measurements</h3>
        <p class="text-sm text-subdued">Please provide your measurements in centimeters</p>
      </div>
      
      <div class="v-stack gap-3">
        <div class="form-field">
          <label for="measurement-waist-${formId}" class="form-label">
            Waist (cm) <span class="text-error">*</span>
          </label>
          <input 
            type="text" 
            id="measurement-waist-${formId}"
            name="properties[Waist]"
            form="${formId}"
            class="input"
            placeholder="e.g., 70"
            required
          />
        </div>

        <div class="form-field">
          <label for="measurement-hips-${formId}" class="form-label">
            Hips (cm) <span class="text-error">*</span>
          </label>
          <input 
            type="text" 
            id="measurement-hips-${formId}"
            name="properties[Hips]"
            form="${formId}"
            class="input"
            placeholder="e.g., 90"
            required
          />
        </div>

        <div class="form-field">
          <label for="measurement-height-${formId}" class="form-label">
            Height (cm) <span class="text-error">*</span>
          </label>
          <input 
            type="text" 
            id="measurement-height-${formId}"
            name="properties[Height]"
            form="${formId}"
            class="input"
            placeholder="e.g., 165"
            required
          />
        </div>
      </div>

      <div class="made-to-measure-note">
        <svg class="icon icon-info" width="16" height="16" viewBox="0 0 16 16" fill="none">
          <circle cx="8" cy="8" r="7" stroke="currentColor" stroke-width="1.5"/>
          <path d="M8 11V7M8 5V5.01" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
        </svg>
        <p class="text-xs">Please ensure measurements are accurate. We'll use these to create your custom piece.</p>
      </div>
    `;

    return container;
  }
}

// Initialize the handler
if (typeof window !== 'undefined') {
  new MadeToMeasureHandler();
}